import {isWedChildrenElt, isWedSlotElt, IWedChildrenElt, IWedDisplayElt, IWedletModel, IWedPerms, IWedSelector, IWedSlotElt, WED} from "/@back@/edit/wed/wedCore.js";
import {isWedEditor, IWedEditor, IWedMgrPointer, WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {isWedletSingleElt, IWedletSingleElt, OffView} from "/@back@/edit/wed/wedlets/wedletSingleElt.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {DOM, ENodeType} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IJmlNode, IJmlObj, IJmlSet, IJmlSubSet, JML, JmlSubSetIterator} from "/@lib@/commons/xml/jml.js";
import {IXAddr, IXAddrRange, XA} from "/@lib@/commons/xml/xAddr.js";
import {IDocBatch} from "/@lib@/edit/docHolder.js";
import {IXmlMsg, XmlDeleteMsg, XmlInsertMsg, XmlStrMsg} from "/@lib@/edit/ot/xmlHouse.js";
import {EAnnotLevel, EFuzzyType, ISkAnnot, ISkStructDef, isSkAnnotFocuser, isSkStructDef} from "/@lib@/edit/schema/schemaAnnots.js";
import {ISkSearchAnnot} from "/@lib@/edit/schema/schemaSearch.js";
import {IWedDiffMargin} from "/@back@/edit/wed/wedlets/diff/diffTags.js";
import {LANG} from "/@lib@/commons/lang.js";
import {IBoxElement} from "/@back@/edit/wed/wedlets/box/boxTags.js";
import {AccelKeyMgr, IAccelKeyMgrPointer} from "/@lib@/commons/actions.js";
import {CLIPBOARD} from "/@lib@/commons/clipboard.js";
import {ActionBtn} from "/@back@/commons/widgets/buttons.js";


/**
 * Un IWedlet représente l'édition d'un fragment d'un contenu : un Element, Attribute, Comment, Text.
 *
 * Chaque IWedlet assume à son niveau l'instanciation et la gestion de ses IWedlet fils.
 * Il peut décider de ne pas les instancier (optimisation de la construction de la page en fonction du viewPort),
 * ou en instancier plusieurs pour les mêmes struct fils : body, head d'un block...
 *
 * Chaque IWedlet gère le dispatch des évènements dans ses fils en fonction de chaque api :
 * Msg OT, selection/focus, annotations, résultat d'une recherche, parcours erreur, etc.
 * Toutes ces fonctions sont définies par interfaces que les IWedlet doivent implémenter en sus.
 */
export interface IWedlet {

	/**
	 * Adresse du rattachement de ce wedlet au flux xml.
	 * Si le wedlet est virtuel, retourne son point d'insertion.
	 */
	readonly wedAnchor: IXAddr

	/** Partie terminale de IWedlet.wedAnchor. null pour le weldet bindé au document ou si le wedlet est virtuel. */
	xaPart: number | string | null


	/** Wedlet container, ou null si c'est le wedlet racine de l'editeur. */
	wedParent: IParentWedlet | null

	/** Gestionnaire central de l'éditeur. */
	wedMgr: WedMgr

	/** Modèle associé à ce wedlet. */
	model: IWedletModel

	/**
	 * Pour les bind dont le modèle est de type élément ou attribut et est anonyme,
	 * mémorise le name (input user ou issu de la config display).
	 * Usage classique : wedlet.wedNodeName || wedlet.model.nodeName
	 */
	wedNodeName?: string

	/** cf IVirtualisableWedlet. ici pour simplification d'écriture. */
	isVirtual(): this is IVirtualisableWedlet

	/**
	 * Ce bind est-il jugée non renseigné (= vide ou valeur par défaut), impliquant
	 * qu'il pourrait être supprimé sans changement logique signifiant.
	 */
	isEmpty(): boolean

	/**
	 * Modification dans un noeud descendant à ce wedlet.
	 * Méthode appelée sur tous les wedlets ancêtres, au-dessus du container de la modif.
	 * Sur le wedlet container de la modif, cette méthode n'est appelée que s'il n'implémente pas IParentWedlet,
	 * sinon ce sont les méthodes plus spécialisées de IParentWedlet qui sont appelées.
	 */
	updateInDescendants?(msg: IXmlMsg): void

	/** Appelé à la suppression du wedlet par son contexte parent. */
	onDelete?(): void

	/** Focus ce wedlet. Méthode optionnelle. */
	focusWedlet?(): void

	/** Appelé après chaque cycle de chgt de ce wedlet (contenu / liste des fils). */
	doCustomAdjust?(): void
}

/**
 * Interface à implémenter pour les wedlet susceptibles de contenir des wedlets fils.
 * Wedlets bindés avec un document ou un élement.
 */
export interface IParentWedlet extends IWedlet {

	/**
	 * Recherche un wedlet fils. Dans le cas de wedlet fils forkés (ie ce parent référence plusieurs wedlets fils pour le même noeud logique),
	 * les options peuvent servir à sélectionner la bonne branche. Sinon, un tableau des fils est retourné et le wedlet d'offset 0
	 * est celui de la branche considérée comme principale (OFindWedletOptions.mainBranch.
	 */
	findWedletChild?(xaPart: number | string, options?: OFindWedletOptions): IChildWedlet | IChildWedlet[]

	/**
	 *
	 * @param from Si -1 et que ce wedlet bind un élément, commence par les attributs.
	 * @param len Nombre d'éléments à parcourir, Infinity pour parcourir jusqu'au dernier fils.
	 * @param visitor
	 * @param options A spécifier...
	 */
	visitWedletChildren?(from: number, len: number, visitor: (this: void, wedlet: IChildWedlet) => 'stop' | void, options?: OVisitWedletOptions): 'stop' | void

	/**
	 * Insert un noeud fils (hors attribut) à ce wedlet.
	 * Cette méthode doit :
	 * - trouver le wedletModel correspondant à ce noeud fils,
	 * - instancier le wedlet selon ce modèle,
	 * - insérer sa représentation HTML dans le dom (à sa bonne place, avec le bon slotName...),
	 * - l'initialiser avec ce contenu  via IChildWedlet.bindWithNode(...).
	 */
	insertChildNode?(xaOffest: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void>

	/**
	 * Doit être appelé après un à n appels à insertChildNode() pour indiquer la fin d'une séquence d'insertion.
	 * Permet de gérer l'insertion des virtuals en une seule passe (optimisation).
	 */
	onChildNodesInserted?(): void

	/**
	 * Remplace un bind fils existant.
	 * Utilisé par le BoxOffView.
	 * TODO A utiliser aussi si un bind anonyme doit être transformé en un bind connu (et changer peut-être de wedChildren / slot).
	 * TODO Pour attribut anonyme -> attribut connu ajouter une méthode ?
	 */
	replaceChildBind?(xaOffest: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void>

	/**
	 * Insert un attribut fils à ce wedlet.
	 * Cette méthode doit :
	 * - trouver le wedletModel correspondant à cet attribut,
	 * - instancier le wedlet selon ce modèle,
	 * - insérer sa représentation HTML dans le dom (à sa bonne place, avec le bon slotName...),
	 * - l'initialiser avec ce contenu  via IChildWedlet.bindWithAttr(...).
	 */
	insertAttrNode?(nameAttr: string, value: string): void | Promise<void>

	deleteAttrNode?(nameAttr: string): void

	deleteChildNodes?(xaOffest: number, count: number): void

	/**
	 * Appelé par le wedlet fils qui est sur le point de devenir vide (noeud texte ou attribut) ou d'être supprimé.
	 * Permet à ce wedlet parent d'indiquer qu'il est préférable de supprimer tout le container parent s'il devient inutile.
	 * @return null ou le container ancêtre à supprimer.
	 */
	onChildEmptied?(willBeEmptied: (xa: IXAddr) => boolean): IXAddr

	/**
	 * Injection d'un élément externe au contenu mais assimilable à un contenu fils.
	 * Permet au wedlet de gérer l'insertion au "bon endroit" en fonction de la nature
	 * à laquelle l'élément à insérer est assimilable.
	 * Usages :
	 * - Matérialisation des points d'insertion à l'import de contenus (drag session, etc.)
	 * - Insertions d'annotations de type diff.
	 *
	 * @param before Insert avant ce wedlet fils.
	 * @param after Insert juste après ce wedlet fils (utile si before null).
	 */
	injectForeignChild?(similarTo: Node | IJmlObj | ISkStructDef, elt: HTMLElement, before?: IWedlet, after?: IWedlet, options?: Dict<any>): void
}

export function isParentWedlet(w: IWedlet): w is IParentWedlet {return w && ("findWedletChild" in w)}

/**
 * Interface à implémenter pour les wedlet racine des éditeurs.
 * Wedlets bindés avec un document ou un élement.
 */
export interface IRootWedlet extends IParentWedlet {
	/**
	 * @param jmlChildren Contenu du document ou du fragment de document à éditer ou noeuds fils de l'élément racine à éditer
	 * @param jmlRoot Noeud Element racine (null si on édite un document ou un fragment de document).
	 * @param xaRoot Adresse dans le document complet de l'élément racine ou du fragment à éditer (null si on édite un document).
	 */
	bindRoot(jmlChildren: IJmlSubSet, jmlRoot?: IJmlNode, xaRoot?: IXAddr): void | Promise<void>
}

/**
 * Interface pour tous les wedlets autres que les wedlets racine des éditeurs, susceptibles d'être fils d'un wedlet.
 * Wedlets bindés avec un élement. Pour les attributs, textes et commentaires, cf ICharsWedlet qui étend cette interface.
 */
export interface IChildWedlet extends IWedlet {

	/**
	 * Associe un contenu de type node (hors attribut) à ce wedlet.
	 */
	bindWithNode(xaOffset: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void>
}

/**
 * Interface pour tous les wedlets bindables avec un noeud terminal contenant une chaine de caractère :
 * texte, attribut, commentaire.
 */
export interface ICharsWedlet extends IChildWedlet, ICharsUpdate {

	/**
	 * Associe un attribut à ce wedlet.
	 */
	bindWithAttr(nameAttr: string, value: string): void | Promise<void>
}

/** (!) Retourne true si le wedlet est bien bindé à un modèle de wedlet textuel (Text, Comment, Attribute) en plus d'implémenter l'interface. */
export function isCharsWedletBind(w: IWedlet | ICharsWedlet): w is ICharsWedlet {
	if (!w || !("bindWithAttr" in w)) return false;
	const type = w.model.nodeType;
	return type === ENodeType.text || type === ENodeType.attribute || type === ENodeType.comment;
}

/**
 * Wedlet issu d'un wed:display (IWedDisplayElt) du modèle wed.
 * Permet au wedlet parent de gérer l'affichage/masquage de ce wedlet en fonction des conditions parent.
 */
export interface IVirtualisableWedlet extends IChildWedlet, IWedletSingleElt {

	/** Contexte de l'affichage forcé de ce wedlet. */
	displayCtx: IWedDisplayElt

	/**
	 * Par opposition à IChildWedlet.bindWithNode() et ICharsWedlet.bindWithAttr(),
	 * associe ce wedlet à un noeud virtual.
	 */
	bindAsVirtual(): void

	/** Retourne le IWedlet.xaPart correspondant à son emplacement virtuel et son point d'insertion (ou son xaPart si le noeud n'est pas virtuel). */
	getVirtualXaPart?(): number | string

	/** Supprime cet élément virtual. Attention, ne DOIT être utilisé que si isVirtual()==true. */
	deleteVirtualWedlet(): void

	/**
	 * Crée un nouveau noeud en vue de concrétiser ce wedlet virtual.
	 * Ne peut être utilisé sur un attribut ou un document.
	 * Permet en particulier sur un élément d'ajouter des attributs (xml:space, role...)
	 */
	newJml(): IJmlNode

	/**
	 * Appelé en cas de détection d'une récursion infinie dans les structures virtuelles.
	 * Doit affecter à this.element un élément HTML de type bouton pour enclencher la récursivité
	 * à la demande.
	 * Attention bindAsVirtual() n'est pas appelé dans ce cas particulier. Il faut donc assumer les init
	 * nécessaires ici comme this.xaPart = null.
	 */
	switchToRecursiveVirtualButton?(): void
}

/** (!) Retourne true si ce wedlet est associé à IWedDisplayElt, pas si la classe implémente IVirtualisableWedlet. */
export function isDisplayedWedlet(w: IWedlet): w is IVirtualisableWedlet {return w && (w as IVirtualisableWedlet).displayCtx != null}


/** Wedlet référencant des forks du même noeud logique. */
export interface IForksWedlet extends IWedlet {

	/**
	 * Visite les forks de ce noeud logique.
	 * Note : cette méthode n'est pas appelée si options.mainBranch=true, donc inutile de tester cette option dans cette méthode.
	 */
	visitWedletForks(visitor: (this: void, wedlet: IWedlet) => 'stop' | void, options?: OVisitWedletOptions): 'stop' | void
}

export function isForksWedlet(w: IWedlet): w is IForksWedlet {return w && ('visitWedletForks' in w)}


/** Interface pour les wedlets suceptible d'être la cible d'un lien (errorBar, outline...)*/
export interface ITargetableWedlet extends IWedlet {
	highlightFromLink(blockPos: 'nearest' | 'start'): Promise<void>
}

export function isTargetableWedlet(wedlet: any): wedlet is ITargetableWedlet {return wedlet && ("highlightFromLink" in wedlet)}

/**
 * Gestion du mode d'édition d'un wedlet qui peut-être caché, en lecture ou éditable.
 */
export interface IEditableWedlet extends IWedlet {

	/**
	 * A minima l'implémentation du widget de la prop 'editMode' DOIT prendre en compte l'état de l'éditeur WedMgr.readOnly,
	 * mais peut aussi intégrer des mécanismes de permissions localisées dans l'arbre d'édition.
	 */
	readonly editMode: EWedletEditMode

	/**
	 * Ce wedlet peut indiquer un editMode que le wedlet descendant passé en paramètre DEVRAIT ou DOIT prendre en compte.
	 *
	 * @param target si null interroge pour n'importequel wedlet descendant (désactivation btn d'insertion fils)
	 * @see WEDLET.resolveEditMode()
	 */
	getEditModeForDesc?(target: IWedlet | null): IWedletEditModeHint | undefined

	/**
	 * Ce mode peut évoluer dynamiquement suite à un évènment externe (édition concurrente posant un verrou,
	 * changement de permission du user courant, etc.). En cas d'un tel évènement, la méthode refreshEditMode()
	 * doit alors être appelée récursivement sur tous les wedlets pour opérer les changements graphiques.
	 *
	 * @see WEDLET.refreshEditMode(root:IWedlet)
	 */
	refreshEditMode(): void
}

export function isEditableWedlet(wedlet: IWedlet): wedlet is IEditableWedlet {return wedlet && ("editMode" in wedlet)}

export interface IWedletEditModeHint {
	editMode: EWedletEditMode
	must?: boolean
}

export const enum EWedletEditMode {
	/** non spécifié. */
	na = 0,

	/** Mode normal. */
	write = 1, //bit 0

	/** lecture seule, widgets focusables. */
	read = 2, //bit 1

	/** lecture seule et sans widgets focusables. */
	readStatic = 6, //bit 2 + bit read à 1

	/** widget masqué. */
	hidden = 8 //bit 3
}

/** Labels pour constituer la valeur de l'attribut HTML edit-mode dans les wedlets. */
export const EWedletEditModeLabel = [null, null, "r", null, null, null, "rs", null, "h"];

/**
 * Api générique pour capter une action du clipboard.
 * Peut être implémenté par un Wedlet qui peut tranférer à un elt html ou autre, etc.
 *
 * @return si true l'action a été réalisée.
 */
export interface ICustomClipboard {
	onClipboard?(act: 'copy' | 'cut' | 'paste', ev?: Event, options?: Dict<any>): Promise<boolean>
}

export function isCustomClipboard(obj: any): obj is ICustomClipboard {return obj && typeof obj.onClipboard === "function"}


/** Listerners des wedlets pour l'injection des annotations. */
export interface ISkAnnotListener {
	/**
	 * @param xaTarget Correspond à `annot.anchor || annot.start`
	 */
	onAddedSkAnnot(annot: ISkAnnot, xaTarget: IXAddr): void;

	/**
	 * @param xaTarget Correspond à `annot.anchor || annot.start`
	 */
	onRemovedSkAnnot(annot: ISkAnnot, xaTarget: IXAddr): void;
}

export function isSkAnnotListener(lstn: any): lstn is ISkAnnotListener {return lstn && ("onAddedSkAnnot" in lstn)}


/** Interface pour les IWedlet et les customElts qui gèrent l'édition d'une valeur (attribut, texte, commentaire) */
export interface ICharsUpdate {

	insertChars?(from: number, chars: string, msg: IXmlMsg): void

	deleteChars?(from: number, len: number, msg: IXmlMsg): void

	replaceChars?(chars: string, msg: IXmlMsg): void
}

export interface ICharsHolder extends ICharsUpdate {

	/**
	 * La valeur est-elle jugée insignifiante (whitespaces) ou égale à la valeur par défaut, impliquant
	 * que ce contenu pourrait être supprimé.
	 */
	isEmpty?(): boolean
}


/** CustomElement bindé à un IWedlet. */
export interface IElementWedlet extends CustomElement, ICharsHolder {

	readonly wedlet: IWedlet

	/** Widgets collapsables. */
	collapsed?: boolean

	/** IWedChildrenElt qui est à l'origine de l'insertion de cet élément. */
	fromChildrenElt?: IWedChildrenElt

	/** Définitions des slots d'injection des fils disponibles. */
	readonly childrenElts?: IWedChildrenElt[]

	/**
	 * Configuration d'un élément du wedlet à partir de son template issu du fichier wed.
	 */
	configWedletElt(tpl: Element, wedlet: IWedlet): void

	/**
	 * @param val IJmlNode pour les éléments, textes, comment; string pour la valeur d'un attribut; null si bind virtuel.
	 * @param children utilisé par les élements des wedlets exploitant toute la descendance.
	 */
	refreshBindValue(val: IJmlNode | string | null, children?: IJmlSubSet): void

	/** Mathode appelée par le wedlet en cas de changement de IEditableWedlet.editMode. */
	setEditMode?(mode: EWedletEditMode): void

	/**
	 * A implémenter uniquement pour des widgets qui auraient leurs propres permisions associées, indépendantes
	 * des permissisons de lecture/écriture définies au niveau du Weldet.
	 * Utilisation : boutons d'actions externes à l'édition comme une intercation CID.
	 */
	reassessPerms?(): void

	/**
	 * Notifié après l'initialisation et après que un ou n wedlets fils (noeuds et/ou attributs) aient été insérés supprimés ou remplacés.
	 * Offre un point d'entrée optimisé (appelé une seule fois) pour mettre à jour l'ihm de cet élément
	 * parent (ex: TxtRoot, injection des para virtuals).
	 * Méthode optionnelle.
	 */
	onChildWedletsChange?(): void

	/** Pour les wedletElt créant un contexte racine de stylage, fournit le host au sein desquels ajouter les wedlets fils. */
	delegatedHost?: HTMLElement

	/** Setter du delegatedHost qui dispatchera un event 'delegate-host' au connectedCallback()
	 * (pour permettre l'ajout de listeners à des events confinés au sein du host).
	 */
	setDelegatedHost?(delegatedHost: HTMLElement): void

}

export const IS_EltWedlet = function (elt: any): elt is IElementWedlet {return elt && ("configWedletElt" in elt)};

//export const IS_EltWedletReal = function (elt: any) {return IS_EltWedlet(elt) && !elt.wedlet.isVirtual()} as INodeFilter<IElementWedlet>;

export function findElementWedlet(from: Node, excludeThis?: boolean): IElementWedlet | null {
	let result = excludeThis ? from.parentNode : from;
	while (result && !IS_EltWedlet(result)) {
		if (isWedEditor(result)) return null;
		result = result.parentNode || (result as ShadowRoot).host;
	}
	return result as IElementWedlet;
}


/**
 * CustomElement réprésentant une annotation.
 * Utilisé uniquement pour les annotations injectées dans un interstice,
 * ie les annots de type Missing et diffForeign.
 *
 */
export interface IElementSkAnnot extends CustomElement {
	skAnnot: ISkAnnot
}

export const IS_EltSkAnnot = function (elt: any): elt is IElementSkAnnot {return elt && ("skAnnot" in elt)};


/**
 * Fournisseur du IWedSelector à utiliser dans ce contexte d'un élement WED.
 * Méthode statique à déclarer sur les classes des customElements des wedlets.
 * Si retourne null, laisse l'appelant choisir son IWedSelector par défaut.
 *
 * @param elt élément ancêtre fournisseur potentiel du du selector.
 * @param chidrenElt balise wed:children pour lequel trouver le selector.
 *        Permet d'obtenir des informations de filtrage supplémentaires (role d'une balise de texte virtuel, ...)
 *        Non renseigné si la balise wed:children n'est pas déclarée explicitiement.
 *
 * @see WEDLET.findWedSelector()
 */
export interface IWedSelectorFactory extends Constructor<IElementWedlet> {
	buildWedSelector(elt: Element, chidrenElt?: IWedChildrenElt): IWedSelector
}

export function findWedEditor(from: Node): IWedEditor {
	return DOMSH.findLogicalFlatParentOrSelf(from, null, isWedEditor);
}

/** Symbaol à ajouter aux element qui possèdent un scroll. */
const SCROLCTN = Symbol("SCROLCTN");

export function markAsScrollCtn(n: HTMLElement): HTMLElement {
	(n as any)[SCROLCTN] = true;
	return n;
}

export function isScrollCtn(n: Node | null): n is HTMLElement {return n && (SCROLCTN in n)}

export function findScrollCtn(from: Node): HTMLElement {
	return DOMSH.findFlatParentEltOrSelf(from, null, isScrollCtn);
}


export interface IFindInAreaWedlet extends IWedlet {
	/** Rect occupé par ce wedlet et sa decsendance. */
	wedletRect?: DOMRectReadOnly | null/*pas de restriction rect*/ | undefined /*comportement par défaut*/

	findInArea(cb: (wedletArea: IWedletArea) => 'next' | 'nextSibling' | 'stop', area: DOMRectReadOnly, visitOptions?: OVisitWedletOptions): void | 'stop'
}

export function isFindAreaWedlet(w: IWedlet): w is IFindInAreaWedlet {return w && ('findInArea' in w)}

export interface IWedletArea {
	wedlet: IWedlet
	/** null si l'élement porteur est "display:contents" : le navigateur ne fournit pas l'info. */
	intersectRect?: DOMRectReadOnly
}


/**
 * Contexte pour une action relative à un wedlet.
 * Note : ne peut être le wedlet lui-même pour permettre la mise en cache des focusBar et un actionContext stable
 * dont les propriétés (focusedElt et wedlet) sont dynamiquement modifiés en fonction du chgt de focus.
 */
export interface IWedletActionCtx extends IWedMgrPointer, IRegPointer<any>, IAccelKeyMgrPointer<any> {
	focusedElt: HTMLElement
	wedlet: IWedlet
}

export class WedletActionCtxBase implements IWedletActionCtx {
	focusedElt: HTMLElement;

	get reg(): IReg<any> {return this.wedMgr.reg}

	get wedMgr(): WedMgr {return this.wedlet.wedMgr}

	get wedlet(): IWedlet {return findElementWedlet(this.focusedElt).wedlet}

	constructor(elt: HTMLElement) {
		this.focusedElt = elt;
	}
}

export class WedletActionCtx extends WedletActionCtxBase {

	accelKeyMgr: AccelKeyMgr<any>

	constructor(elt: HTMLElement, accelKeyMgr?: AccelKeyMgr<any>) {
		super(elt);
		this.accelKeyMgr = accelKeyMgr;
	}
}

/**
 * Indicateur d'annotation d'erreur insérée dans l'éditeur.
 */
export class WedAnnotErr extends HTMLElement {
	skAnnots: ISkAnnot[];
	holder: HTMLElement & IWedAnnotPointer;

	/**
	 * Activation d'un controle asynchrone de la validité des annot lorsqu'elles sont ancrés sur des descendants:
	 * indispensable en cas de suppression d'un container de cet ancrage descendant.
	 */
	subAnnotsCheck: (wedMgr: WedMgr, annots: ISkAnnot[], deadline: RequestIdleDeadline) => void;

	/** 1er init en même temps que l'insertion. */
	initAnnot(holder: HTMLElement & IWedAnnotPointer, skAnnot: ISkAnnot, wedlet: IWedlet, isSubAnnot: boolean): this {
		this.skAnnots = [];
		this.holder = holder;
		holder.style.position = "relative";
		holder.wedAnnotErr = this;
		wedlet.wedMgr.reg.installSkin(this.localName, this.attachShadow(DOMSH.SHADOWDOM_INIT));
		(holder.shadowRoot || holder).appendChild(this);
		this.addAnnot(skAnnot, isSubAnnot, wedlet);
		return this;
	}

	addAnnot(skAnnot: ISkAnnot, isSubAnnot: boolean, wedlet: IWedlet) {
		if (isSubAnnot) {
			if (!this.subAnnotsCheck) {
				this.subAnnotsCheck = this.asyncSkAnnots.bind(this);
				wedlet.wedMgr.listeners.on("asyncSkAnnots", this.subAnnotsCheck);
			}
		}
		if (skAnnot.result !== null) this.skAnnots.push(skAnnot); //Toute annot sauf les AnnotFuture résolues en null.
		if (skAnnot.future) skAnnot.future.then((an: ISkAnnot | null) => {
			if (an === null) this.removeAnnot(skAnnot);
			else this.refresh();
		});
		this.refresh();
	}

	protected asyncSkAnnots(wedMgr: WedMgr, annots: ISkAnnot[], deadline: RequestIdleDeadline): void | ((wedMgr: WedMgr, annots: ISkAnnot[], deadline: RequestIdleDeadline) => void) {
		if (deadline.timeRemaining() < 5) return this.subAnnotsCheck;
		//Listener async pour purge des "sub" annots, ie les annots ancrées sur des descendants dont des containers ont été suppprimés.
		let found = false;
		for (let a of this.skAnnots) {
			const anchor = a.anchorNode;
			if (anchor instanceof Attr ? !anchor.ownerElement?.isConnected : !anchor?.isConnected) this.removeAnnot(a);
			else found = true;
		}
		if (!found) {
			wedMgr.listeners.removeListener("asyncSkAnnots", this.subAnnotsCheck);
			this.subAnnotsCheck = null;
		}
	}

	removeAnnot(skAnnot: ISkAnnot): boolean {
		const idx = this.skAnnots.indexOf(skAnnot);
		if (idx < 0) return false;
		this.skAnnots.splice(idx, 1);
		this.refresh();
		return true;
	}

	removeAll() {
		this.holder.removeAttribute("annot");
		this.holder.wedAnnotErr = null;
		this.holder = null; //help gc
		this.remove();
	}

	protected refresh() {
		const l = this.skAnnots.length;
		if (l === 0) {
			//on supprime.
			this.holder.removeAttribute("annot");
			this.holder.wedAnnotErr = null;
			this.remove();
		} else if (l === 1) {
			this.title = this.skAnnots[0].getLabel();
			this.holder.setAttribute("annot", this.className = this.skAnnots[0].level.name);
		} else {
			this.title = l > 5 ? `${l} erreurs` : this.skAnnots.map(an => an.getLabel()).join("\n");
			this.holder.setAttribute("annot", this.className = this.skAnnots.reduce((prev: EAnnotLevel, c) => prev.max(c.level), EAnnotLevel.info).name);
		}
	}
}

REG.reg.registerSkin('wed-annot-err', 1, /* language=CSS */ `
	:host {
		min-width: .8rem;
		min-height: .8rem;
		position: absolute;
		left: -.3rem;
		top: -.1rem;
		cursor: default;
	}

	:host(.error) {
		background: center / contain no-repeat url(/@skin@/edit/wed/error.svg);
	}

	:host(.warning) {
		background: center / contain no-repeat url(/@skin@/edit/wed/warning.svg);
	}

	:host(.info) {
		background: center / contain no-repeat url(/@skin@/edit/wed/info.svg);
	}
`);

window.customElements.define("wed-annot-err", WedAnnotErr);


/**
 * Indicateur d'annotation de recherche insérée dans l'éditeur.
 */
export class WedAnnotSearch extends HTMLElement {
	skAnnots: ISkSearchAnnot[];
	holder: HTMLElement & IWedAnnotPointer;

	initSearchAnnot(holder: HTMLElement & IWedAnnotPointer, wedlet: IWedlet): this {
		this.holder = holder;
		DOM.setStyle(holder, "position", "relative");
		holder.wedAnnotSearch = this;
		(holder.shadowRoot || holder).appendChild(this);
		wedlet.wedMgr.reg.installSkin(this.localName, this.attachShadow(DOMSH.SHADOWDOM_INIT));
		this.holder.setAttribute("annot-search", "");
		return this;
	}

	addAnnot(skAnnot: ISkSearchAnnot) {
		if (this.skAnnots) this.skAnnots.push(skAnnot);
		else {
			this.skAnnots = [skAnnot];
			this.title = skAnnot.getLabel();
		}
	}

	removeAnnot(skAnnot: ISkSearchAnnot): boolean {
		this.removeAll();
		return true;
	}

	removeAll() {
		this.holder.wedAnnotSearch = null;
		this.holder.removeAttribute("annot-search");
		this.remove();
		this.holder = null;
	}
}

REG.reg.registerSkin('wed-annot-search', 1, /* language=CSS */ `
	:host {
		position: absolute;
		min-width: 100%;
		min-height: 100%;
		right: 0;
		top: 0;
		cursor: default;
		background-color: var(--edit-search-bgcolor);
		opacity: .3;
		border: 1px solid var(--edit-search-bgcolor);
		border-radius: .2rem;
		pointer-events: none;
	}
`);

window.customElements.define("wed-annot-search", WedAnnotSearch);

export interface IWedAnnotPointer {
	wedAnnotErr?: WedAnnotErr
	wedAnnotSearch?: WedAnnotSearch
	wedAnnotDiff?: IWedDiffMargin
}


export interface OFindWedletOptions {
	/** Retourne le dernier ancêtre trouvé, jamais null. */
	lastAncestorIfNone?: boolean
	/**
	 * [input+output] Si lastAncestorIfNone==true et wedletNotFoundDepth==-1, la profondeur du 1er wedlet non trouvé sera retournée.
	 * Si aucun wedlet trouvé (hors hiérarchie) ou si le wedlet correspondant au path entier est trouvé, cette propriété n'est pas modifiée.
	 * @see WEDLET.findWedlet()
	 */
	wedletNotFoundDepth?: number
	/** Limite la recherche à la branche principale. Garanti que IParentWedlet.findWedletChild() retourne un seul wedlet, pas un tableau. */
	mainBranch?: boolean
	/**
	 * Lorsqu'un ancêtre est trouvé avant d'atteindre la cible, cette cb est appelée.
	 * Uniquement appelé dans WEDLETS.findWedlets() et WEDLETS.findWedlet().
	 * Pas appelé dans IWedlet.findWedletChild(), IWedlet.visitWedletChildren() ni dans IForksWedlet.visitWedletForks().
	 */
	cbOnAncestors?: (anc: IWedlet) => void
	/** Force la construction de la descendance si nécessiare (Wedlets off-view). */
	forceFetch?: boolean
	/** [output] Si forceFetch=true, promises retournés à résoudre pour obtenir le résultat. */
	forceFetchPromises?: Promise<IWedlet | IWedlet[]>[]
	/** [output] La recherche du wedlet a-t-elle provoqué une modification du contenu (decollapse, Wedlets off-view...) */
	contentUpdated?: boolean
}

export interface OVisitWedletOptions {
	/** Limite le parcours à la branche principale.*/
	mainBranch?: boolean

	/** Inclus les wedlets dans un état virtuel. */
	includeVirtuals?: boolean

	/** Force la construction de la descendance si nécessiare (Wedlets off-view). */
	forceFetch?: boolean

	/** [output] Promises à attendre issus de forceFetch=true. */
	forceFetchPromises?: Promise<any>[]
}

/**
 * Wedlet de base pour des forks d'un IForksWedlet.
 */
export abstract class WedletFork implements IWedlet {

	protected constructor(public mainWedlet: IWedlet) {
		WEDLET.addFork(mainWedlet, this)
	}

	get model(): IWedletModel {return this.mainWedlet.model};

	get wedAnchor(): IXAddr {return this.mainWedlet.wedAnchor};

	get wedMgr(): WedMgr {return this.mainWedlet.wedMgr};

	get wedParent(): IParentWedlet | null {return this.mainWedlet.wedParent};

	get xaPart(): number | string | null {return this.mainWedlet.xaPart};

	isVirtual(): this is IVirtualisableWedlet {return this.mainWedlet.isVirtual()}

	isEmpty(): boolean {return this.mainWedlet.isEmpty()}

}

export namespace WEDLET {

	/** Transforme un IWedlet en IForksWedlet ou ajoute un fork supplémentaire à un IForksWedlet. */
	export function addFork(mainWedlet: IWedlet, forkWedlet: IWedlet): IForksWedlet {
		const w = mainWedlet as IForksWedlet;
		const subVisit = w.visitWedletForks;
		if (subVisit) {
			w.visitWedletForks = function (this: IForksWedlet, visitor: (this: void, wedlet: IWedlet) => 'stop' | void, options?: OVisitWedletOptions): 'stop' | void {
				if (visitor(forkWedlet) === "stop") return "stop";
				return subVisit.call(this, visitor, options);
			}
		} else {
			w.visitWedletForks = function (this: IForksWedlet, visitor: (this: void, wedlet: IWedlet) => 'stop' | void): 'stop' | void {
				if (visitor(forkWedlet) === "stop") return "stop";
			}
		}
		return w;
	}

	/** Rafraichit le mode d'édition de toute la hiérarchie de widgets. */
	export function refreshEditMode(root: IWedlet): void {
		if (isEditableWedlet(root)) root.refreshEditMode();
		if (isParentWedlet(root)) root.visitWedletChildren(-1, Infinity, refreshEditMode, VISITOPTIONS_includeVirtuals);
	}

	/**
	 * Résout l'editMode en fonction du contexte.
	 * Règles de priorité de construction du mode :
	 * 1 - Si wedMgr.readOnly==true impose toujours à minima EWedletEditMode.read
	 * 2 - IEditableWedlet.getEditModeForDesc() est évalué sur chaque ancêtre qui peut imposer un EWedletEditMode minimum.
	 *     Tous les ancêtres sont parcourus, le EWedletEditMode max est retenu.
	 * 3 - le EWedletEditMode issu des perms de target ou, à défaut, le 1er ancêtre qui posède une config de perm "cascade"
	 *     est retenu. Autrement dit les perms déclarés du wedlets le plus bas l'emporte.
	 * Le max de ces 3 algo constitue le EWedletEditMode final.
	 *
	 * @param target Wedlet cible pour lequel résoudre l'editMode.
	 */
	export function resolveEditMode(target: IWedlet): EWedletEditMode {
		const wedMgr = target.wedMgr;
		const reg = wedMgr.reg;
		let permMode = getEditModePerms(target.model.perms, reg);
		let ancMode = EWedletEditMode.na;
		let checkAnc = true;
		let p = target.wedParent as IEditableWedlet;
		while (p) {
			if (permMode === EWedletEditMode.na) permMode = getEditModeCascadPerms(p.model.perms, reg);
			if (checkAnc && 'getEditModeForDesc' in p) {
				const mode = p.getEditModeForDesc(target);
				if (mode !== undefined) {
					if (mode.must) {
						ancMode = mode.editMode;
						if (permMode !== EWedletEditMode.na) break; //Plus la peine de remonter.
						checkAnc = false;
					} else {
						if (ancMode < mode.editMode) ancMode = mode.editMode;
					}
				}
			}
			p = p.wedParent as IEditableWedlet;
		}
		return Math.max(wedMgr.readOnly ? EWedletEditMode.read : EWedletEditMode.write, ancMode, permMode);
	}

	function getEditModePerms(perms: IWedPerms, reg: IReg<any>): EWedletEditMode {
		if (perms) {
			if ('cascadRead' in perms && !reg.hasPerm(perms.cascadRead)) return EWedletEditMode.hidden;
			if ('localWrite' in perms) {
				const v = perms.localWrite;
				if (v === true) return EWedletEditMode.write;
				if (v === false) return EWedletEditMode.read;
				return reg.hasPerm(v) ? EWedletEditMode.write : EWedletEditMode.read;
			}
		}
		return EWedletEditMode.na;
	}

	function getEditModeCascadPerms(perms: IWedPerms, reg: IReg<any>): EWedletEditMode {
		if (perms) {
			if ('cascadRead' in perms && !reg.hasPerm(perms.cascadRead)) return EWedletEditMode.hidden;
			if ('cascadWrite' in perms) {
				const v = perms.cascadWrite;
				if (v === true) return EWedletEditMode.write;
				if (v === false) return EWedletEditMode.read;
				return reg.hasPerm(v) ? EWedletEditMode.write : EWedletEditMode.read;
			}
		}
		return EWedletEditMode.na;
	}

	export const READ = Object.freeze({editMode: EWedletEditMode.read, must: false}) as IWedletEditModeHint;
	export const READ_MUST = Object.freeze({editMode: EWedletEditMode.read, must: true}) as IWedletEditModeHint;
	export const READSTATIC_MUST = Object.freeze({editMode: EWedletEditMode.readStatic, must: true}) as IWedletEditModeHint;
	export const HIDDEN_MUST = Object.freeze({editMode: EWedletEditMode.hidden, must: true}) as IWedletEditModeHint;

	/** Evalue si une action en écriture est autorisée sur ce wedlet. */
	export function isWritableWedlet(wedlet: IWedlet): boolean {
		let w = wedlet;
		while (w) {
			if ("editMode" in w) return (w as IEditableWedlet).editMode === EWedletEditMode.write;
			w = (w as IChildWedlet).wedParent;
		}
		return !wedlet.wedMgr.readOnly;
	}

	export const FINDOPTIONS_lastAncestorIfNone = Object.freeze({lastAncestorIfNone: true}) as OFindWedletOptions;

	/**
	 * Recherche le 1er wedlet fils correspondant à xa.
	 * ATTENTION en cas de fork, d'autres wedlet bindant ce noeud pourrait exister dans l'editor; cette
	 * méthode retourne le wedlet de la branche principale.
	 */
	export function findWedlet(this: void, root: IWedlet, xa: IXAddr, options?: OFindWedletOptions): IWedlet {
		const rootXa = root.wedAnchor;
		if (XA.isAncOrEquals(rootXa, xa)) {
			let res: IWedlet = root;
			for (let i = rootXa.length; res && i < xa.length; i++) {
				if (res !== root) options?.cbOnAncestors?.call(null, res);
				if (isParentWedlet(res)) {
					const next = res.findWedletChild(xa[i], options);
					if (!next) {
						if (options && options.lastAncestorIfNone) {
							if (options.wedletNotFoundDepth === -1) options.wedletNotFoundDepth = i;
							return res;
						}
						return null;
					}
					res = Array.isArray(next) ? next[0] : next;
				} else {
					if (options && options.lastAncestorIfNone) {
						if (options.wedletNotFoundDepth === -1) options.wedletNotFoundDepth = i;
						return res;
					}
					return null;
				}
			}
			return res;
		}
		return null;
	}

	/** Extrait le 1er wedlet de OFindWedletOptions.forceFetchPromises. */
	export function getFirstWedlet(wedlets: (IWedlet | IWedlet[])[]) {
		for (const w of wedlets) {
			if (Array.isArray(w)) return w[0];
			if (w) return w;
		}
	}

	/**
	 * Retrouve tous les wedlets de l'editeur d'une adresse logique (intégrant les forks).
	 * Si options.lastAncestorIfNone=true, des wedlet ancêtres de certaines branches peuvent
	 * être listées avec des wedlets de l'adresse demandée issus d'autres branches.
	 * Note : OFindWedletOptions.wedletNotFoundDepth non renseigné ici.
	 */
	export function findWedlets(this: void, root: IWedlet, xa: IXAddr, options: OFindWedletOptions | null, result: IWedlet[] = []): IWedlet[] {
		const rootXa = root.wedAnchor;
		const addWedlet: (arr: IWedlet[], wedlet: IWedlet) => void = (options?.mainBranch) ? function (arr: IWedlet[], wedlet: IWedlet) {
			arr.push(wedlet);
		} : function (arr: IWedlet[], wedlet: IWedlet) {
			arr.push(wedlet);
			if (isForksWedlet(wedlet)) wedlet.visitWedletForks(arr.push.bind(arr));
		};
		if (XA.isAnc(rootXa, xa)) {
			let parentList: IWedlet[] = [];
			options?.cbOnAncestors?.call(null, root);
			addWedlet(parentList, root);
			for (let i = rootXa.length, s = xa.length - 1; i < s; i++) {
				const childList: IWedlet[] = [];
				for (let k = 0; k < parentList.length; k++) {
					const w = parentList[k];
					if (isParentWedlet(w)) {
						const ch = w.findWedletChild(xa[i], options);
						if (!ch) {
							if (options && options.lastAncestorIfNone) result.push(w);
						} else if (Array.isArray(ch)) {
							for (const w of ch) {
								options?.cbOnAncestors?.call(null, w);
								addWedlet(childList, w);
							}
						} else {
							options?.cbOnAncestors?.call(null, ch);
							addWedlet(childList, ch);
						}
					} else if (options && options.lastAncestorIfNone) result.push(w);
				}
				parentList = childList;
			}
			for (let k = 0; k < parentList.length; k++) {
				const p = parentList[k];
				if (isParentWedlet(p)) {
					const ch = p.findWedletChild(XA.last(xa), options);
					if (!ch) {
						if (options && options.lastAncestorIfNone) result.push(p);
					} else if (Array.isArray(ch)) {
						for (const w of ch) addWedlet(result, w);
					} else {
						addWedlet(result, ch);
					}
				} else if (options && options.lastAncestorIfNone) result.push(p);
			}
		} else if (XA.isEquals(rootXa, xa)) addWedlet(result, root);
		return result;
	}

	export const VISITOPTIONS_mainBranch = Object.freeze({mainBranch: true}) as OVisitWedletOptions;
	export const VISITOPTIONS_includeVirtuals = Object.freeze({includeVirtuals: true}) as OVisitWedletOptions;

	export function visitSequence(this: void, root: IRootWedlet, from: IXAddr, len: number, visitor: (this: null, wedlet: IWedlet) => 'stop' | void, options?: OVisitWedletOptions): 'stop' | void {
		if (options && options.forceFetch) throw Error("options.forceFetch not implemented");
		WEDLET.findWedlets(root, XA.subXa(from, -1), options).forEach((container: IWedlet) => {
			if (isParentWedlet(container)) {
				if (container.visitWedletChildren(XA.offset(from, -1), len, visitor, options) === 'stop') return 'stop';
			}
			if ((!options || !options.mainBranch) && isForksWedlet(container)) {
				container.visitWedletForks((w) => {
					if (isParentWedlet(w)) {
						if (w.visitWedletChildren(XA.offset(from, -1), len, visitor, options) === 'stop') return 'stop';
					}
				})
			}
		});
	}

	export function visitRange(this: void, root: IRootWedlet, xaRange: IXAddrRange, visitor: (this: null, wedlet: IWedlet) => 'stop' | void, options?: OVisitWedletOptions): 'stop' | void {
		//console.log("range::: " + JSON.stringify(xaRange));
		const seqs = XA.range2Seqs(xaRange);
		//console.log("seqs:::" + JSON.stringify(seqs), seqs, xaRange.toString());
		for (let i = 0; i < seqs.length; i++) {
			const s = seqs[i];
			if (WEDLET.visitSequence(root, s.start, s.len, visitor, options) === 'stop') return 'stop';
		}
	}


	export function countChildren(this: void, wedlet: IParentWedlet): number {
		let countChildren = 0;
		wedlet.visitWedletChildren(0, Infinity, (wedlet: IChildWedlet) => {countChildren++}, WEDLET.VISITOPTIONS_mainBranch);
		return countChildren;
	}

	export function applyUpdate(this: void, root: IRootWedlet, msg: IXmlMsg) {
		if (!root) return; //editor sans rrot (outline...)
		const xaCtn = XA.up(msg.xa);
		const wedlets = WEDLET.findWedlets(root, xaCtn, {
			lastAncestorIfNone: true, cbOnAncestors: (wedlet) => {
				if ('updateInDescendants' in wedlet) wedlet.updateInDescendants(msg);
			}
		});
		const s = wedlets.length;
		if (s > 0) {
			for (let i = 0; i < s; i++) {
				const ctn = wedlets[i];
				if (!WEDLET.isWedletBindXa(ctn, xaCtn) || !isParentWedlet(ctn)) {
					if ('updateInDescendants' in ctn) ctn.updateInDescendants(msg);
				} else if (msg instanceof XmlInsertMsg) {
					let from = XA.last(msg.xa) as number;
					if (typeof msg.jml === 'string') {
						if (isCharsWedletBind(ctn)) {
							ctn.insertChars(from, msg.jml, msg);
						} else {
							ctn.insertChildNode(from++, msg.jml, null);
							ctn.onChildNodesInserted();
						}
					} else {
						const it = new JmlSubSetIterator(msg.jml);
						while (it.next()) {
							ctn.insertChildNode(from++, it.currentNode as IJmlNode, it.currentChildren);
						}
						ctn.onChildNodesInserted();
					}
				} else if (msg instanceof XmlDeleteMsg) {
					const from = XA.last(msg.xa) as number;
					if (isCharsWedletBind(ctn)) {
						ctn.deleteChars(from, msg.len, msg);
					} else {
						ctn.deleteChildNodes(from, msg.len);
					}
				} else if (msg instanceof XmlStrMsg) {
					const last = XA.last(msg.xa);
					const val = msg.val;
					if (val == null) {
						if (typeof last === 'string') (ctn as IParentWedlet).deleteAttrNode(last);
						else Error("Can not delete node with XmlStrMsg");
					} else {
						function applyOnAtt(wedletAtt: IChildWedlet | IChildWedlet[]) {
							if (Array.isArray(wedletAtt)) wedletAtt.forEach(applyOnAtt);
							else {
								if (wedletAtt) {
									(wedletAtt as ICharsWedlet).replaceChars(val, msg);
								} else {
									if (typeof last === 'string') (ctn as IParentWedlet).insertAttrNode(last, val);
									else Error("Can not insert node with XmlStrMsg");
								}
							}
						}

						applyOnAtt(ctn.findWedletChild(last));
					}
				}
			}
		} else {
			//aucun wedlet trouvé.
			if (msg instanceof XmlDeleteMsg && XA.isAncOrEquals(msg.xa, root.wedAnchor)) {
				//Aïe, suppr de notre contexte root, cet éditeur est mort !
				//L'env parent de cet éditeur doit prendre en compte cet évènement.
				root.wedMgr.wedEditor.dispatchEvent(new CustomEvent('wed-editor-root-deleted'));
				root.wedMgr.detachDocHolder();
			}
		}
	}

	export function isWedletBindXa(this: void, wedlet: IWedlet, xa: IXAddr): boolean {
		for (let i = xa.length - 1; i >= 0; i--) {
			if (wedlet.xaPart !== xa[i]) return false;
			const parent = wedlet.wedParent;
			if (!parent) {
				//on est sur le wedletRoot.
				const rootXa = wedlet.wedAnchor;
				if (rootXa.length - 1 !== i--) return false;
				for (; i >= 0; i--) if (rootXa[i] !== xa[i]) return false;
				return true;
			}
			wedlet = parent;
		}
		return true;
	}

	export function getWedletDepth(this: void, wedlet: IWedlet): number {
		let depth = 0;
		while (wedlet) {
			if (wedlet.wedParent == null) depth += wedlet.wedAnchor.length;
			else depth += 1;
			wedlet = wedlet.wedParent;
		}
		return depth;
	}

	/**
	 * Traite les attributs `skin` et `skinOver`
	 * @param tpl
	 * @param target
	 * @param wedlet
	 * @param defaultSkin
	 */
	export function installSkins(this: void, tpl: Element, target: ShadowRoot, wedlet: IWedlet, defaultSkin?: string) {
		const skin = tpl.getAttribute("skin") || defaultSkin;
		const skinsOver = tpl.getAttribute("skinOver");
		if (skin || skinsOver) {
			const reg = wedlet.wedMgr.reg;
			if (skin) reg.installSkin(skin, target);
			if (skinsOver) skinsOver.split(" ").forEach(skinOver => reg.installSkin(skinOver, target));
		}
	}

	export function cloneWedletContent(this: void, targetRoot: IElementWedlet, tpl: Element, wedlet: IWedletSingleElt, asShadow: boolean): IElementWedlet[] {
		if (!tpl.hasChildNodes()) return null;
		let subEltWedlets: IElementWedlet[];
		let from: Node = tpl.firstChild;
		let target: Node;
		if (asShadow) {
			if (targetRoot.shadowRoot) {
				//shadowRoot déjà créé
				target = targetRoot.shadowRoot;
			} else if (!from.nextSibling && !tpl.hasAttribute('skin') && (isWedChildrenElt(from) || isWedSlotElt(from))) {
				//Optimisation: le seul fils est un wed:children ou wed:slot, et pas de skin, on ne crée pas de shadowTree
				target = targetRoot;
			} else {
				target = targetRoot.attachShadow(DOMSH.SHADOWDOM_INIT);
				installSkins(tpl, target as ShadowRoot, wedlet);
			}
		} else {
			target = targetRoot;
		}
		while (from) {
			if (from.nodeType === Node.TEXT_NODE) {
				target.appendChild(document.importNode(from, false));
			} else if (isWedChildrenElt(from)) {
				const host = wedlet.elementHost;
				if (target !== host && !isWedSlotElt(from.parentNode)) createSlot(from, target); //on est en shadow, on cree le <slot>
			} else if (isWedSlotElt(from)) {
				if (from.firstChild) {
					if (target !== wedlet.elementHost) createSlot(from, target); //on est en shadow, on cree le <slot>
					from = from.firstChild;
					continue;
				}
			} else {
				const newCh: Node = target.appendChild(document.importNode(from, false));
				if (IS_EltWedlet(newCh)) {
					newCh.configWedletElt(from as Element, wedlet);
					if (!subEltWedlets) subEltWedlets = [];
					subEltWedlets.push(newCh);
				} else {
					if (from.firstChild) {
						target = newCh;
						from = from.firstChild;
						continue;
					}
				}
			}
			while (!from.nextSibling) {
				from = from.parentNode;
				if (from === tpl) return subEltWedlets;
				if (!isWedSlotElt(from)) target = target.parentNode;
			}
			from = from.nextSibling;
		}
		return subEltWedlets;
	}

	/** Clone simple d'un contenu, sans gestion des wed:children, wed:slot...*/
	export function cloneSimpleContent(this: void, targetRoot: Node, tpl: Element) {
		for (let ch = tpl.firstChild; ch; ch = ch.nextSibling) targetRoot.appendChild(document.importNode(ch, true));
	}


	/** Recherche un <wed:children> matchant précisément un noeud, null sinon. */
	export function findChildrenEltForNodeStrict(this: void, childrenElts: IWedChildrenElt[], jmlNode: IJmlNode): IWedChildrenElt {
		if (!childrenElts) return null;
		for (let i = 0; i < childrenElts.length; i++) {
			const child = childrenElts[i];
			if (child.wedMatcher.matchNode(jmlNode)) return child;
		}
		return null;
	}

	/** Recherche le meilleur <wed:children> pour insérer un noeud. */
	export function findChildrenEltForNode(this: void, childrenElts: IWedChildrenElt[], jmlNode: IJmlNode, parentWedlet: IParentWedlet, xaOffset: number): IWedChildrenElt {
		if (!childrenElts) return null;
		if (jmlNode != null) for (let i = 0; i < childrenElts.length; i++) {
			const child = childrenElts[i];
			if (child.wedMatcher.matchNode(jmlNode)) return child;
		}
		//Pas trouvé de match explicite, on vérifie qu'on a au moins un childrenElt qui n'est pas strict
		let chEltNotStrict: IWedChildrenElt;
		for (let i = 0; i < childrenElts.length; i++) {
			if (!childrenElts[i].wedMatchStrict) {
				chEltNotStrict = childrenElts[i];
				break;
			}
		}
		if (!chEltNotStrict) return null; //aucun childrenElt non strict.
		// on recherche par rapport à xaOffset
		// (on prend le même IChildrenElt que celui actuellement au même offset).
		let ch = parentWedlet.findWedletChild(xaOffset);
		if (Array.isArray(ch)) ch = ch[0];
		if (isWedletSingleElt(ch) && ch.element.fromChildrenElt && !ch.element.fromChildrenElt.wedMatchStrict) return ch.element.fromChildrenElt;
		if (xaOffset > 0) {
			//pas trouvé on essaye sur le précédent offset.
			ch = parentWedlet.findWedletChild(xaOffset - 1);
			if (Array.isArray(ch)) ch = ch[0];
			if (isWedletSingleElt(ch) && ch.element.fromChildrenElt && !ch.element.fromChildrenElt.wedMatchStrict) return ch.element.fromChildrenElt;
		}
		return chEltNotStrict; //rien trouvé, on prend le 1er non strict
	}

	/** Recherche un <wed:children> matchant un attr. */
	export function findChildrenEltForAttr(this: void, childrenElts: IWedChildrenElt[], atrrName: string): IWedChildrenElt {
		if (!childrenElts) return null;
		for (let i = 0; i < childrenElts.length; i++) {
			const child = childrenElts[i];
			if (child.wedMatcher.matchAttr(atrrName)) return child;
		}
		return null;
	}

	/** Recherche un <wed:children> matchant un comment. */
	export function findChildrenEltForComment(this: void, childrenElts: IWedChildrenElt[]): IWedChildrenElt {
		if (!childrenElts) return null;
		for (let i = 0; i < childrenElts.length; i++) {
			const child = childrenElts[i];
			if (child.wedMatcher.matchNode(commentTpl)) return child;
		}
		return childrenElts[0];
	}

	const commentTpl = {'': JML.COMMENT};

	export function createSlot(this: void, childrenElt: IWedChildrenElt | IWedSlotElt, parentSlot: Node): Element {
		const slot = document.createElement("slot");
		//A voir l'utilité : le slot n'est pas stylable !
		//les attributs sans ns sont pour wed, on recopie les attr qui ont un ns xhtml
		// let atts = children.attributes;
		// for (let i = 0, s = atts.length; i < s; i++) {
		// 	let att = atts.item(i);
		// 	if (att.namespaceURI === DOM.XHTML_NS) slot.setAttribute(att.name, att.value);
		// }
		slot.setAttribute("name", childrenElt.wedSlotName);
		return parentSlot.appendChild(slot);
	}

	/** Insère les attributs d'un noeud.*/
	export function appendAttWedlets(this: void, wedlet: IParentWedlet, node: IJmlNode, insAtt: (this: IParentWedlet, nameAttr: string, value: string) => void | Promise<void>, promises?: Promise<void>[]): Promise<void>[] {
		if (!JML.isElt(node)) return;
		for (const name in node) {
			if (name) {
				const promise = insAtt.call(wedlet, name, node[name]);
				if (promise) {
					if (!promises) promises = [promise];
					else promises.push(promise);
				}
			}
		}
		return promises;
	}


	/**
	 * Crée une structure de données JML susceptible de concrétiser un affichage virtuel.
	 * Retourne le plus haut wedlet virtuel dans la hierarchie qui sera "remplie".
	 */
	export function insertDatasFromDisplay(this: void, wedlet: IVirtualisableWedlet, batch: IDocBatch | null, newChildren?: string | IJmlSet, newName?: string): IVirtualisableWedlet {
		const b = batch || wedlet.wedMgr.docHolder.newBatch();
		let parent = wedlet.wedParent;
		let datas: IJmlSet;
		if (wedlet.model.nodeType === ENodeType.attribute) {
			//cas particulier de l'attribut
			const attName = newName || wedlet.wedNodeName || wedlet.model.nodeName;
			if (!parent || !parent.isVirtual()) {
				const xa = XA.append(wedlet.wedParent.wedAnchor, attName);
				b.setAttr(xa, newChildren as string || "");
				if (!batch) b.doBatch();
				return wedlet;
			}
			wedlet = parent as IVirtualisableWedlet;
			parent = wedlet.wedParent;
			const eltNode = wedlet.newJml() as IJmlObj;
			eltNode[attName] = newChildren as string || "";
			datas = [eltNode];
		} else if (wedlet.model.nodeType === ENodeType.text) {
			datas = [newChildren || ""];
		} else if (wedlet.model.nodeType === ENodeType.element) {
			datas = [newName ? {'': newName} : wedlet.newJml()];
			if (newChildren) datas.push(Array.isArray(newChildren) ? newChildren : [newChildren]);
			buildJmlWithSiblings(
				newName ? {'': newName} : wedlet.newJml(),
				newChildren ? Array.isArray(newChildren) ? newChildren : [newChildren] : null,
				wedlet);
		} else {
			throw Error("wedlet.model.nodeType unknown : " + wedlet.model.nodeType);
		}
		while (parent && parent.isVirtual()) {
			wedlet = parent as IVirtualisableWedlet;
			datas = buildJmlWithSiblings(wedlet.newJml(), datas, wedlet);
			parent = wedlet.wedParent;
		}
		b.insertJml(wedlet.wedAnchor, datas);
		if (!batch) b.doBatch();

		function buildJmlWithSiblings(obj: IJmlNode, children: IJmlSet | null, w: IWedlet): IJmlSet {
			const result: IJmlSet = [];
			if (isWedletSingleElt(w)) {
				//Succession de frères en card N fondés sur le même modèle (exemple: feedback des assmnt répétés).
				const model = w.model;
				let elt = DOM.findPreviousSibling(w.element, IS_EltWedlet);
				while (elt && elt.wedlet.isVirtual() && elt.wedlet.model === model) {
					//XXX égalité des modèles ou égalité du eltName & eltType ?
					result.push(elt.wedlet.newJml());
					elt = DOM.findPreviousSibling(elt, IS_EltWedlet);
				}
			}
			result.push(obj);
			if (children) result.push(children);
			return result;
		}

		return wedlet;
	}

	/**
	 * Controle qu'un noeud virtuel puisse bien être concrétisé à cet offset.
	 * Gère le fait qu'un élément offView peut-etre mal placé et ne doit pas être pris en compte.
	 */
	export function checkVirtualOffset(this: void, virtualChild: IElementWedlet, offset: number): boolean {
		let prev = virtualChild.previousElementSibling;
		let min = 0;
		let max = 0;
		while (prev) {
			if (IS_EltWedlet(prev) && !prev.wedlet.isVirtual()) {
				const xaPart = prev.wedlet.xaPart;
				if (typeof xaPart === 'number') {
					if (prev.wedlet instanceof OffView) {
						max = Math.max(max, xaPart + 1);
					} else {
						min = xaPart + 1;
						max = Math.max(max, min);
					}
				}
			}
			prev = prev.previousElementSibling;
		}
		return offset >= min && offset <= max;
	}

	export function buildVirtualPath(this: void, wedlet: IWedlet): Array<IJmlObj | '#' | '@'> | null {
		if (!wedlet.isVirtual()) return null;
		const path = [] as Array<IJmlObj | '#' | '@'>;
		do {
			switch (wedlet.model.nodeType) {
			case ENodeType.element :
				path.push(wedlet.newJml() as IJmlObj);
				break;
			case ENodeType.text:
				path.push('#');
				break;
			case ENodeType.attribute:
				path.push('@');
				break;
			case ENodeType.comment:
				path.push({'': JML.COMMENT});
				break;
			}
			wedlet = wedlet.wedParent;
		} while (wedlet.isVirtual());
		return path.reverse();
	}

	/** Recherche les noeuds virtuels ou masqués après un point donné. */
	export function findVirtualWedletsAfter(this: void, elt: IElementWedlet, result?: IWedlet[]): IWedlet[] {
		const rootElt = (elt.wedlet as IWedletSingleElt).element || elt;
		for (let next = DOM.findNextSibling(rootElt, IS_EltWedlet); next; next = DOM.findNextSibling(next, IS_EltWedlet)) {
			if (next.wedlet.isVirtual() || next.hidden) {
				if (!result) result = [next.wedlet];
				else result.push(next.wedlet);
			} else if (next.wedlet.model.nodeType !== ENodeType.comment) {
				break;
			}
		}
		return result;
	}

	/** Recherche les noeuds virtuels ou masqués avant un point donné. */
	export function findVirtualWedletsBefore(this: void, elt: IElementWedlet, addElt?: boolean): IWedlet[] {
		let result: IWedlet[];
		if (addElt && elt.wedlet.isVirtual()) result = [elt.wedlet];
		const rootElt = (elt.wedlet as IWedletSingleElt).element || elt;
		for (let next = DOM.findPreviousSibling(rootElt, IS_EltWedlet); next; next = DOM.findPreviousSibling(next, IS_EltWedlet)) {
			const nextWedlet = next.wedlet;
			if (nextWedlet.isVirtual() || next.hidden) {
				if (!result) result = [nextWedlet];
				else result.push(nextWedlet);
			} else if (nextWedlet.model.nodeType !== ENodeType.comment) {
				break;
			}
		}
		return result;
	}

	/** Recherche les wedlets fils virtuels. */
	export function findAllChildrenVirtualWedlets(this: void, elt: IElementWedlet): IWedlet[] {
		const ch: IWedlet[] = [];
		(elt.wedlet as IParentWedlet).visitWedletChildren(-1, Infinity, w => {if (w.isVirtual()) ch.push(w)}, WEDLET.VISITOPTIONS_includeVirtuals);
		return ch;
	}

	export function isTextBindBefore(from: IElementWedlet): boolean {
		if (from.wedlet.model.nodeType === ENodeType.text) return true;
		const rootElt = (from.wedlet as IWedletSingleElt).element || from;
		const prev = DOM.findPreviousSibling(rootElt, IS_EltWedlet);
		return prev && prev.wedlet.model.nodeType === ENodeType.text;
	}

	export function isTextBindAfter(from: IElementWedlet): boolean {
		if (from.wedlet.model.nodeType === ENodeType.text) return true;
		const rootElt = (from.wedlet as IWedletSingleElt).element || from;
		const next = DOM.findNextSibling(rootElt, IS_EltWedlet);
		return next && next.wedlet.model.nodeType === ENodeType.text;
	}

	/**
	 * Nettoie une liste de ISkStructDef insérable en éliminant ceux déjà présent dans une liste de noeuds virtuels ou qui sont masqués.
	 * @return le nombre restant de stuct possibles.
	 */
	export function cleanupStructList(this: void, structs: ISkStructDef[], virtualsOrHidden: IWedlet[], excludeText?: boolean): number {
		if (!structs) return 0;
		if (!virtualsOrHidden && !excludeText) return structs.reduce((total, rule) => rule != null ? total + 1 : total, 0);
		let count = structs.length;
		for (let i = 0; i < structs.length; i++) {
			const struct = structs[i];
			if (!struct) {
				count--;
				continue;
			}
			if (excludeText && struct.structType === ENodeType.text) {
				structs[i] = null;
				count--;
			} else if (virtualsOrHidden && !LANG.in(struct.structType, EFuzzyType.attributes, EFuzzyType.elements)) {
				for (let k = 0; k < virtualsOrHidden.length; k++) {
					const virtual = virtualsOrHidden[k];
					if (struct.structMatch(virtual.model.nodeType, virtual.wedNodeName || virtual.model.nodeName)) {
						structs[i] = null;
						count--;
						break;
					}
				}
			}
		}
		return count;
	}

	/**
	 * Parcourt un arbre de config,
	 * extrait les elts wed:children (sans les traverser),
	 * recherche le wedSelector adéquat en fonction du contexte.
	 * ATTENTION : penser à les trier selon leur priorité du matching
	 * (array.sort(WED.SORT_ChildrenElts);).
	 */
	export function extractChildrenNodes(this: void, from: Element, wedSelectorBuilder?: (this: void, elt: Element, forChidrenElt?: IWedChildrenElt) => IWedSelector): IWedChildrenElt[] {
		let array: IWedChildrenElt[];
		const tw = from.ownerDocument.createTreeWalker(from, NodeFilter.SHOW_ELEMENT, {
			acceptNode(n: Node): number {
				if (isWedChildrenElt(n)) {
					if (!array) array = [n];
					else array.push(n);
					if (wedSelectorBuilder) n.wedSelector = wedSelectorBuilder(n.parentElement, n);
					return NodeFilter.FILTER_REJECT;
				}
				return n.namespaceURI === DOM.XHTML_NS || n.namespaceURI === WED.WED_NS ? NodeFilter.FILTER_SKIP : NodeFilter.FILTER_REJECT;
			}
		});
		tw.nextNode();
		return array;
	}

	export function findVirtualWedletFrom(this: void, display: IWedDisplayElt, parent: Element, before?: Element): IVirtualisableWedlet {
		for (let ch = before ? before.previousElementSibling : parent.lastElementChild; ch; ch = ch.previousElementSibling) {
			if (IS_EltWedlet(ch)) {
				const wedlet = ch.wedlet;
				if (isDisplayedWedlet(wedlet) && wedlet.displayCtx === display) {
					return wedlet.isVirtual() ? wedlet : null;
				}
			}
		}
		return null;
	}

	/**
	 * Recherche dans un bind wed, le wedSelector adéquat en fonction des customElements contextuels.
	 */
	export function findWedSelector(this: void, elt: Element, forChidrenElt?: IWedChildrenElt): IWedSelector {
		while (elt && elt.namespaceURI !== WED.WED_NS) {
			if (elt.localName.indexOf('-') >= 0) {
				const ctor = window.customElements.get(elt.localName) as Constructor<Element>;
				if (ctor && "buildWedSelector" in ctor) return (ctor as IWedSelectorFactory).buildWedSelector(elt, forChidrenElt);
			}
			elt = elt.parentElement;
		}
		return null;
	}

	export function onChildEmptied(this: void, wedldet: IParentWedlet, willBeEmptied: (xa: IXAddr) => boolean, isRemovableCustom?: () => boolean): IXAddr {
		let empty = true;
		let xa = XA.append(wedldet.wedAnchor, 0);
		wedldet.visitWedletChildren(-1, Infinity, (wedlet: IChildWedlet) => {
			if (!willBeEmptied(XA.setAtDepth(xa, -1, wedlet.xaPart)) && !wedlet.isEmpty()) {
				//ce wedlet n'est pas dans la liste de ceux en cours de suppr et ce wedlet fils n'est pas empty.
				empty = false;
				return 'stop';
			}
		}, WEDLET.VISITOPTIONS_mainBranch);
		if (empty) {
			const wedParent = wedldet.wedParent;
			xa = XA.up(xa);//on revient sur this.wedAnchor
			if (isRemovableCustom ? !isRemovableCustom() : !wedldet.wedMgr.docHolder.isRemovable(xa)) {
				//on est vidé mais obligatoire, on n'est pas supprimable, mais peut-etre que notre contexte parent l'est.
				if (wedParent && ('onChildEmptied' in wedParent)) return wedParent.onChildEmptied((subXa: IXAddr) => XA.isEquals(xa, subXa) || willBeEmptied(subXa));
			} else {
				//on est vidé et supprimable.
				if (wedParent && ('onChildEmptied' in wedParent)) {
					//on tente de remonter sur le parent.
					return wedParent.onChildEmptied((subXa: IXAddr) => XA.isEquals(xa, subXa) || willBeEmptied(subXa)) || xa;
				}
				return xa;
			}
		}
		return null; //pas vide ou pas supprimable.
	}

	/** Gère la suppression des ancêtres lors de la suppression d'un range. */
	export function absorbContainersOnDelete(this: void, rootWedlet: IWedlet, range: IXAddrRange): IXAddrRange {
		const result = {start: range.start, end: range.end};
		const wedlet = WEDLET.findWedlet(rootWedlet, XA.up(XA.freeze(result.start)), WEDLET.FINDOPTIONS_lastAncestorIfNone) as IParentWedlet;
		if (wedlet && ('onChildEmptied' in wedlet)) {
			const newStart = wedlet.onChildEmptied((xa: IXAddr) => XA.isInRange(result, xa, false, true));
			if (newStart) {
				result.start = newStart;
				if (XA.isAnc(newStart, result.end)) {
					result.end = XA.newBd(newStart).incrAtDepth(-1, 1).xa;
					return result;
				}
			}
		}
		if (!XA.isInSameSeq(result.start, result.end)) {
			const endWedlet = WEDLET.findWedlet(rootWedlet, XA.up(XA.freeze(result.end)), WEDLET.FINDOPTIONS_lastAncestorIfNone) as IParentWedlet;
			if (endWedlet && ('onChildEmptied' in endWedlet)) {
				const newEnd = endWedlet.onChildEmptied((xa: IXAddr) => XA.isInRange(result, xa, false, true));
				if (newEnd) {
					result.end = XA.incrAtDepth(newEnd, -1, 1);
				}
			}
		}
		return result;
	}

	/** Pour les widget de type enum, checkbox (...) qui remplacent toute la valeur en façon atomique. */
	export function replaceValue(wedlet: IWedlet, oldVal: string | null, newVal: string | null, batch?: IDocBatch) {
		if (!WEDLET.isWritableWedlet(wedlet)) return;
		const b = batch ?? wedlet.wedMgr.docHolder.newBatch();
		if (oldVal) {
			//une val était définie
			if (newVal) {
				b.spliceSequence(XA.append(wedlet.wedAnchor, 0), oldVal.length, newVal);
			} else {
				//Noeud texte vide.
				const thisXa = wedlet.wedAnchor;
				const xa = 'onChildEmptied' in wedlet.wedParent ? wedlet.wedParent.onChildEmptied((subXa: IXAddr) => XA.isEquals(thisXa, subXa)) : null;
				if (xa) b.deleteSequence(xa, 1); //on supprime un contexet ancêtre
				else b.deleteSequence(XA.append(thisXa, 0), oldVal.length);
			}
		} else if (newVal) {
			if (wedlet.isVirtual()) {
				WEDLET.insertDatasFromDisplay(wedlet as IVirtualisableWedlet, b, newVal);
			} else {
				b.insertText(XA.append(wedlet.wedAnchor, 0), newVal);
			}
		} else return;
		if (!batch) b.doBatch();
	}

	export function findWedletInArea(cb: (wedletArea: IWedletArea) => 'next' | 'nextSibling' | 'stop', rootWedlet: IWedlet, area: DOMRectReadOnly, visitOptions?: OVisitWedletOptions): void | 'stop' {
		if (isFindAreaWedlet(rootWedlet)) {
			return rootWedlet.findInArea(cb, area, visitOptions);
		} else if (isParentWedlet(rootWedlet)) {
			if (rootWedlet.visitWedletChildren(-1, Infinity, (w: IWedlet) => {
				if (findWedletInArea(cb, w, area, visitOptions) === 'stop') return 'stop';
			}, visitOptions) === 'stop') return 'stop';
		}
	}

	export async function focusWedlet(xa: IXAddr, wedMgr: WedMgr) {
		const opts: OFindWedletOptions = {forceFetch: true, lastAncestorIfNone: true, mainBranch: true};
		let wedlet = WEDLET.findWedlet(wedMgr.rootWedlet, xa, opts);
		if (opts.forceFetchPromises) {
			wedlet = WEDLET.getFirstWedlet(await Promise.all(opts.forceFetchPromises));
		}
		while (wedlet) {
			if (wedlet.focusWedlet) {
				wedlet.focusWedlet();
				break;
			}
			wedlet = wedlet.wedParent;
		}
	}

	export async function highlightWedletFromLink(xa: IXAddr, wedMgr: WedMgr, blockPos: 'nearest' | 'start') {
		const opts: OFindWedletOptions = {forceFetch: true, lastAncestorIfNone: true, wedletNotFoundDepth: -1, mainBranch: true};
		let wedlet = WEDLET.findWedlet(wedMgr.rootWedlet, xa, opts);
		if (opts.forceFetchPromises) {
			wedlet = WEDLET.getFirstWedlet(await Promise.all(opts.forceFetchPromises));
		}
		if (blockPos === 'nearest' && opts.wedletNotFoundDepth !== -1 && typeof xa[opts.wedletNotFoundDepth] === "number" && isParentWedlet(wedlet)) {
			//descendant pas trouvé (donc supprimé), on descend au dernier fils.
			wedlet.visitWedletChildren(0, Infinity, (c) => {wedlet = c}, WEDLET.VISITOPTIONS_mainBranch);
		}
		while (wedlet) {
			if (isTargetableWedlet(wedlet)) return wedlet.highlightFromLink(blockPos);
			wedlet = wedlet.wedParent;
		}
	}

	export function ensureContainersUncollapsed(elt: Element) {
		DOMSH.findFlatParentElt(elt, findWedEditor(elt)?.rootNode, (n: Element): n is Element => {
			if ((n as IElementWedlet).collapsed) (n as IBoxElement).collapsed = false;
			return false;
		});
	}

	export async function focusAnnot(wedMgr: WedMgr, annot: ISkAnnot) {
		const opts: OFindWedletOptions = {forceFetch: true, lastAncestorIfNone: true};
		let wedletOwner = WEDLET.findWedlet(wedMgr.rootWedlet, annot.anchor || annot.start, opts);
		if (opts.forceFetchPromises) wedletOwner = WEDLET.getFirstWedlet(await Promise.all(opts.forceFetchPromises));
		if (!isSkAnnotFocuser(wedletOwner) || !wedletOwner.focusSkAnnot(annot)) {
			while (wedletOwner) {
				if (isTargetableWedlet(wedletOwner)) return wedletOwner.highlightFromLink('nearest');
				wedletOwner = wedletOwner.wedParent;
			}
		}
	}

	export function clearAnnots(elt: Element & IWedAnnotPointer) {
		if (elt.wedAnnotErr) elt.wedAnnotErr.removeAll();
		if (elt.wedAnnotSearch) elt.wedAnnotSearch.removeAll();
		if (elt.wedAnnotDiff) elt.wedAnnotDiff.removeDiffWidget();
	}

	export function matchModel(simil: IJmlObj | ISkStructDef | Node | Attr, model: IWedletModel) {
		//note : en virtuel, pas de model.nodeType fuszzy / unkonwn.
		if (isSkStructDef(simil)) {
			if (simil.structType !== model.nodeType) return false;
			return model.nodeType === ENodeType.element || model.nodeType === ENodeType.attribute ? simil.structName === model.nodeName : null;
		} else if (simil instanceof Attr) {
			return model.nodeType === ENodeType.attribute && model.nodeName === simil.nodeName;
		} else if (simil instanceof Node) {
			if (simil.nodeType !== model.nodeType) return false;
			return model.nodeType === ENodeType.element ? simil.nodeName === model.nodeName : null;
		} else {
			if (JML.jmlNode2nodeType(simil) !== model.nodeType) return false;
			return model.nodeType === ENodeType.element ? JML.jmlNode2name(simil) === model.nodeName : null;
		}
	}

	export function isAncestorOrEqual(anc: IWedlet, desc: IWedlet) {
		while (desc) {
			if (anc === desc) return true;
			desc = desc.wedParent;
		}
		return false;
	}

	export async function readClipboard(): Promise<ClipboardItem[] | null> {
		return CLIPBOARD.read(document.body);
	}

	/** Diff en mode lazy loading. */
	export let diffLib: typeof import("/@back@/edit/wed/wedlets/diff/diffTags.js");

	export async function importDiffLib(): Promise<void> {
		if (!diffLib) diffLib = await import("/@back@/edit/wed/wedlets/diff/diffTags.js");
	}
}
